import { PRIORITIES } from '@/core/constants/installation-priorities.constant'
import { permissionCache } from '@/core/services/permission-cache.service'
import { useAuthStore } from '@/modules/auth/stores/auth.store'

export const priority = PRIORITIES.PERMISSION

function createPermissionDirective() {
  return {
    mounted(element, binding) {
      const authStore = useAuthStore()

      function applyPermissionResult(hasAccess) {
        if (!hasAccess) {
          if (binding.modifiers.hide) {
            element.style.display = 'none'
          }
          else if (binding.arg === 'fallback' && binding.value.fallback) {
            // Handle fallback content
            const fallback = binding.value.fallback
            if (typeof fallback === 'string') {
              element.innerHTML = fallback
            }
            else if (typeof fallback === 'function') {
              element.innerHTML = fallback()
            }
          }
          else {
            element.parentNode?.removeChild(element)
          }
        }
        else if (binding.modifiers.hide) {
          element.style.display = ''
        }
      }

      function checkPermissions() {
        const permissions = Array.isArray(binding.value)
          ? binding.value
          : [binding.value]

        if (!binding.modifiers.dynamic) {
          const cachedResult = permissionCache.get(
            permissions,
            binding.modifiers,
          )

          if (cachedResult !== undefined) {
            applyPermissionResult(cachedResult)
            return
          }
        }

        let hasAccess = false

        try {
          if (binding.modifiers.all) {
            hasAccess = permissions.every(permission =>
              authStore.hasPermission(permission),
            )
          }
          else if (binding.modifiers.any) {
            hasAccess = permissions.some(permission =>
              authStore.hasPermission(permission),
            )
          }
          else {
            hasAccess = authStore.hasPermission(permissions[0])
          }

          // Cache the result if not dynamic
          if (!binding.modifiers.dynamic) {
            permissionCache.set(permissions, binding.modifiers, hasAccess)
          }

          applyPermissionResult(hasAccess)
        }
        catch (error) {
          console.error(
            '[Permission Directive] Error checking permissions:',
            error,
          )
          // In case of error, deny access by default
          applyPermissionResult(false)
        }
      }

      checkPermissions()

      if (binding.modifiers.dynamic) {
        const unwatch = watch(
          () => authStore.userPermissions,
          () => {
            checkPermissions()
            // Clear cache on permission changes
            permissionCache.clear()
          },
          { deep: true },
        )

        element._unwatchPermissions = unwatch
      }
    },

    unmounted(element) {
      if (element._unwatchPermissions) {
        element._unwatchPermissions()
      }
    },
  }
}

const defaultOptions = {
  name: 'permission',
  errorHandler: error => console.error('[Permission Directive]:', error),
  onDenied: (element, permission) => {
    console.warn(`Permission denied: ${permission}`)
  },
}

function createPermissionPlugin(options = {}) {
  const config = { ...defaultOptions, ...options }
  const directive = createPermissionDirective()

  return {
    install(app) {
      app.directive(config.name, {
        mounted(element, binding) {
          try {
            directive.mounted(element, binding)
          }
          catch (error) {
            config.errorHandler(error)
          }
        },
        unmounted(element) {
          try {
            directive.unmounted(element)
          }
          catch (error) {
            config.errorHandler(error)
          }
        },
      })

      app.config.globalProperties.$permission = config
    },
  }
}

export function install({ app }) {
  const permissionPlugin = createPermissionPlugin({
    errorHandler: (error) => {
      console.error('[DSS Permission]:', error)
    },
    onDenied: (element, permission) => {
      console.warn(`[DSS Permission] Access denied for: ${permission}`)
    },
  })

  app.use(permissionPlugin)
}

export { createPermissionPlugin }
